import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  X,
  ChevronRight,
  ChevronLeft,
  Rocket,
  AlertTriangle,
  Loader2,
  ShieldCheck,
  Mail,
  Droplets,
} from "lucide-react";
import type { AiAgent } from "@shared/schema";
import { useAiAgentsSurface } from "@/pages/ai-agents-surface";

const BUDGET_WARN_USD = 1000;

interface StateRow {
  state: string;
  stateAbbrev: string;
  cityCount: number;
}
interface CityRow {
  id: string;
  city: string;
  state: string;
  stateAbbrev: string;
  population: number | null;
  rank: number | null;
}
interface BusinessType {
  id: string;
  name: string;
}
interface SelectedCity {
  state: string;
  stateAbbrev: string;
  city: string;
  population: number | null;
}
interface FunnelEstimate {
  cells: number;
  prospects: number;
  withEmail: number;
  deliverable: number;
  qualified: number;
  enrolled: number;
  cost: {
    placesUsd: number;
    emailFinderUsd: number;
    verificationUsd: number;
    qualificationUsd: number;
    totalUsd: number;
  };
  estimatedCostUsd: number;
  estimatedCredits: number;
  rates: {
    avgProspectsPerSearch: number;
    emailFoundRate: number;
    deliverableRate: number;
    qualifiedRate: number;
  };
}

interface OutreachCampaign {
  id: string;
  name: string;
  type: "drip" | "mass";
  status?: string;
}
interface OutreachUser {
  id: string;
  name: string;
}
interface OutreachClientType {
  id: string;
  name: string;
  isDefault: boolean;
}
interface OutreachMeta {
  campaigns: { drip: OutreachCampaign[]; mass: OutreachCampaign[] };
  hasColdDomain: boolean;
  users: OutreachUser[];
  clientTypes?: OutreachClientType[];
}

const fmt = (n: number) => n.toLocaleString("en-US");
const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const cityKey = (c: { stateAbbrev: string; city: string }) =>
  `${c.stateAbbrev}|${c.city}`;

export function LeadOutreachWizard({
  agent,
  open,
  onOpenChange,
  initialCampaignId,
}: {
  agent: AiAgent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCampaignId?: string;
}) {
  const { toast } = useToast();
  const { apiBase, canLaunch, enrollsLeads, allowWarmFallback, supportsMass } =
    useAiAgentsSurface();
  const [step, setStep] = useState(0);

  // Selections
  const [businessTypes, setBusinessTypes] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<Record<string, SelectedCity>>({});
  const [activeStates, setActiveStates] = useState<Set<string>>(new Set());
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  // Deliverability verification is a non-optional compliance guardrail — the
  // server always verifies, so this is a fixed value, not a user toggle.
  const verifyEmails = true;
  const [campaignType, setCampaignType] = useState<"drip" | "mass">("drip");
  const [runName, setRunName] = useState("");
  // Tenant-only: the client type imported leads are tagged with. Empty = let the
  // server fall back to the schema default.
  const [clientType, setClientType] = useState("");

  // Outreach message source (tenant only — platform imports prospects, no send).
  const [messageSource, setMessageSource] = useState<"existing" | "compose">("existing");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  // Warm-fallback acknowledgement (tenant only, shown when no verified cold domain).
  const [warmFallbackAck, setWarmFallbackAck] = useState(false);

  // Reset everything when the dialog closes; pre-populate linked campaign on open.
  useEffect(() => {
    if (!open) {
      setStep(0);
      setBusinessTypes([]);
      setSelectedCities({});
      setActiveStates(new Set());
      setStateSearch("");
      setCitySearch("");
      setCampaignType("drip");
      setRunName("");
      setClientType("");
      setEstimate(null);
      setSpendCapUsd("");
      setMessageSource("existing");
      setSelectedCampaignId("");
      setComposeSubject("");
      setComposeBody("");
      setWarmFallbackAck(false);
      setPendingAddAllStates(new Set());
    } else if (initialCampaignId) {
      // Pre-select the agent's linked drip campaign when the dialog opens.
      setMessageSource("existing");
      setSelectedCampaignId(initialCampaignId);
    }
  }, [open, initialCampaignId]);

  // Step model. The message step (pick existing campaign | compose fresh) and the
  // warm-fallback acknowledgement only apply where this surface actually enrolls
  // + sends (tenant). Task #510 removed the reply-visibility picker — agent leads
  // are admin-only by design, so there is no longer a free-form audience step.
  const stepKeys = useMemo(() => {
    const keys: Array<"types" | "cities" | "options" | "message" | "review"> = [
      "types",
      "cities",
      "options",
    ];
    if (enrollsLeads) keys.push("message");
    keys.push("review");
    return keys;
  }, [enrollsLeads]);
  const stepKey = stepKeys[step] ?? "types";
  const stepLabels: Record<string, string> = {
    types: "Business types",
    cities: "Cities",
    options: "Options",
    message: "Message",
    review: "Review",
  };
  const lastStep = stepKeys.length - 1;

  const { data: types = [], isLoading: typesLoading } = useQuery<BusinessType[]>({
    queryKey: [`${apiBase}/meta/business-types`],
    enabled: open,
  });
  const { data: states = [], isLoading: statesLoading } = useQuery<StateRow[]>({
    queryKey: [`${apiBase}/meta/states`],
    enabled: open,
  });
  // Poll seed-status while the wizard is open and the table might be empty.
  const { data: seedStatus } = useQuery<{ ready: boolean; count: number }>({
    queryKey: [`${apiBase}/meta/cities-ready`],
    enabled: open,
    refetchInterval: (query) => {
      const data = query.state.data as { ready: boolean; count: number } | undefined;
      return data?.ready === false ? 3000 : false;
    },
  });
  const seedReady = seedStatus?.ready !== false;

  // When seeding completes, invalidate the states query so the list refreshes
  // immediately without waiting for the dialog to be reopened.
  useEffect(() => {
    if (seedStatus?.ready === true) {
      queryClient.invalidateQueries({ queryKey: [`${apiBase}/meta/states`] });
    }
  }, [seedStatus?.ready, apiBase]);

  const activeStatesSorted = useMemo(() => [...activeStates].sort(), [activeStates]);

  const { data: cities = [], isFetching: citiesLoading } = useQuery<CityRow[]>({
    queryKey: [`${apiBase}/meta/cities`, activeStatesSorted.join(","), citySearch],
    queryFn: async () => {
      const params = new URLSearchParams({ states: activeStatesSorted.join(","), limit: "1500" });
      if (citySearch.trim()) params.set("q", citySearch.trim());
      const res = await fetch(`${apiBase}/meta/cities?${params}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load cities");
      return res.json();
    },
    enabled: open && activeStatesSorted.length > 0 && seedReady,
  });

  // Outreach reference data: workspace campaigns (pick-existing), cold-domain
  // status (warm-fallback warning), and users (reply-visibility multi-select).
  const { data: outreachMeta } = useQuery<OutreachMeta>({
    queryKey: [`${apiBase}/meta/outreach`],
    enabled: open,
  });
  const allCampaigns = useMemo<OutreachCampaign[]>(
    () => [
      ...(outreachMeta?.campaigns.drip ?? []),
      ...(outreachMeta?.campaigns.mass ?? []),
    ],
    [outreachMeta],
  );
  const clientTypes = outreachMeta?.clientTypes ?? [];

  // Default the client-type choice to the workspace default once meta loads
  // (tenant only). The operator can still change it on the Options step.
  useEffect(() => {
    if (!enrollsLeads || clientType || clientTypes.length === 0) return;
    const def = clientTypes.find((c) => c.isDefault) ?? clientTypes[0];
    if (def) setClientType(def.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollsLeads, clientTypes.length]);

  const selectedList = useMemo(() => Object.values(selectedCities), [selectedCities]);

  const toggleBusinessType = (name: string) =>
    setBusinessTypes((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );

  const toggleCity = (c: CityRow) => {
    const key = cityKey(c);
    setSelectedCities((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else
        next[key] = {
          state: c.state,
          stateAbbrev: c.stateAbbrev,
          city: c.city,
          population: c.population,
        };
      return next;
    });
  };

  const addAllVisible = () =>
    setSelectedCities((prev) => {
      const next = { ...prev };
      for (const c of cities)
        next[cityKey(c)] = {
          state: c.state,
          stateAbbrev: c.stateAbbrev,
          city: c.city,
          population: c.population,
        };
      return next;
    });

  const [pendingAddAllStates, setPendingAddAllStates] = useState<Set<string>>(new Set());

  const addAllForState = async (s: StateRow) => {
    // Activate the state checkbox so its cities appear in the main list too.
    setActiveStates((prev) => {
      if (prev.has(s.stateAbbrev)) return prev;
      const next = new Set(prev);
      next.add(s.stateAbbrev);
      return next;
    });
    // Show "Adding…" on the button while fetching.
    setPendingAddAllStates((prev) => new Set([...prev, s.stateAbbrev]));
    try {
      // Fetch the *complete* unfiltered city list for this one state — no
      // citySearch filter, high enough limit to cover any single state.
      const params = new URLSearchParams({ states: s.stateAbbrev, limit: "5000" });
      const res = await fetch(`${apiBase}/meta/cities?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load cities for state");
      const stateCities: CityRow[] = await res.json();
      setSelectedCities((prev) => {
        const next = { ...prev };
        for (const c of stateCities)
          next[cityKey(c)] = { state: c.state, stateAbbrev: c.stateAbbrev, city: c.city, population: c.population };
        return next;
      });
    } finally {
      setPendingAddAllStates((prev) => {
        const next = new Set(prev);
        next.delete(s.stateAbbrev);
        return next;
      });
    }
  };

  // ── Estimate ────────────────────────────────────────────────────────────
  const [estimate, setEstimate] = useState<FunnelEstimate | null>(null);
  // Per-run USD spend cap — REQUIRED, no default. The run auto-pauses the
  // instant real spend crosses it.
  const [spendCapUsd, setSpendCapUsd] = useState<string>("");
  const estimateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `${apiBase}/runs/estimate`, {
        businessTypes,
        cities: selectedList,
        verifyEmails,
      });
      return (await res.json()) as FunnelEstimate;
    },
    // Clear any prior estimate before recomputing so stale numbers can never be
    // shown (or saved) against a changed scope.
    onMutate: () => setEstimate(null),
    onSuccess: (data) => setEstimate(data),
    onError: () =>
      toast({
        title: "Couldn't calculate the estimate",
        description: "Adjust the scope or try again — no run was saved.",
        variant: "destructive",
      }),
  });

  // Recompute the estimate whenever the review step is shown or scope changes.
  useEffect(() => {
    if (open && stepKey === "review" && businessTypes.length > 0 && selectedList.length > 0) {
      estimateMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, open, businessTypes.length, selectedList.length, verifyEmails]);

  // Whether the tenant message step is satisfied (pick existing campaign OR a
  // complete compose). Platform never sends, so it is always "valid" there.
  const messageValid =
    !enrollsLeads ||
    (messageSource === "existing"
      ? !!selectedCampaignId
      : composeSubject.trim().length > 0 && composeBody.trim().length > 0);

  // Build the create-run payload. Message fields are tenant-only and only sent
  // when complete (a half-filled compose is omitted so a draft can still save).
  const buildRunBody = () => {
    const body: Record<string, unknown> = {
      name: runName.trim() || undefined,
      campaignType,
      scope: {
        businessTypes,
        cities: selectedList,
        verifyEmails,
        // Tenant-only: tag imported leads with the chosen client type.
        ...(enrollsLeads && clientType.trim()
          ? { clientType: clientType.trim() }
          : {}),
      },
    };
    if (enrollsLeads) {
      if (messageSource === "existing") {
        if (selectedCampaignId) body.campaignId = selectedCampaignId;
      } else if (composeSubject.trim() && composeBody.trim()) {
        body.composeSubject = composeSubject.trim();
        body.composeBody = composeBody.trim();
      }
      body.warmFallbackAck = warmFallbackAck;
    }
    return body;
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `${apiBase}/${agent.id}/runs`, buildRunBody());
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiBase] });
      queryClient.invalidateQueries({ queryKey: [apiBase, agent.id, "runs"] });
      toast({
        title: "Run saved as draft",
        description: "Review it before launching — no outreach has been sent.",
      });
      onOpenChange(false);
    },
    onError: () => toast({ title: "Could not create run", variant: "destructive" }),
  });

  const capValue = Number(spendCapUsd);
  const capValid =
    spendCapUsd.trim() !== "" && Number.isFinite(capValue) && capValue > 0;

  // Create the draft, then immediately launch it with the required spend cap.
  const launchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `${apiBase}/${agent.id}/runs`, buildRunBody());
      const run = await res.json();
      const launched = await apiRequest(
        "POST",
        `${apiBase}/runs/${run.id}/launch`,
        { spendCapUsd: capValue },
      );
      return launched.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiBase] });
      queryClient.invalidateQueries({ queryKey: [apiBase, agent.id, "runs"] });
      toast({
        title: "Run launched",
        description: `Capped at ${usd(capValue)}. It auto-pauses the moment real spend crosses the cap.`,
      });
      onOpenChange(false);
    },
    onError: () => toast({ title: "Could not launch run", variant: "destructive" }),
  });

  const canNext =
    (stepKey === "types" && businessTypes.length > 0) ||
    (stepKey === "cities" && selectedList.length > 0) ||
    stepKey === "options" ||
    (stepKey === "message" && messageValid);

  const overBudget = !!estimate && estimate.estimatedCostUsd > BUDGET_WARN_USD;

  // Without a verified cold domain the two surfaces diverge:
  //  • Tenant (allowWarmFallback): outreach can fall back to a warm mailbox, but
  //    the user must acknowledge the reputation risk first.
  //  • Platform (!allowWarmFallback): cold bulk has NO warm fallback — the run is
  //    hard-blocked until the cold relay is configured.
  const coldUnready =
    enrollsLeads && !!outreachMeta && outreachMeta.hasColdDomain === false;
  const needsWarmAck = coldUnready && allowWarmFallback;
  const coldBlocked = coldUnready && !allowWarmFallback;
  const warmAckOk = (!needsWarmAck || warmFallbackAck) && !coldBlocked;

  const STEPS = stepKeys.map((k) => stepLabels[k]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>New Lead Outreach Run — {agent.name}</DialogTitle>
          <DialogDescription>
            Cast a wide net, then sift down to a small, qualified, compliant subset
            before any outreach is sent.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 text-xs">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full font-medium ${
                  i === step
                    ? "bg-primary text-primary-foreground"
                    : i < step
                    ? "bg-primary/20 text-primary"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {i + 1}
              </span>
              <span className={i === step ? "font-medium" : "text-muted-foreground"}>
                {label}
              </span>
              {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            </div>
          ))}
        </div>

        <div className="min-h-[360px] py-2">
          {/* ── Step 1: Business types ──────────────────────────────────── */}
          {stepKey === "types" && (
            <div className="space-y-3">
              <Label>Which business types should this agent prospect for?</Label>
              {typesLoading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
              ) : types.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No business types defined yet. Add them under Operations → Business Types.
                </p>
              ) : (
                <ScrollArea className="h-[300px] rounded-md border p-3">
                  <div className="grid grid-cols-2 gap-2">
                    {types.map((t) => (
                      <label
                        key={t.id}
                        className="flex items-center gap-2 rounded-md p-2 hover-elevate cursor-pointer"
                        data-testid={`option-business-type-${t.id}`}
                      >
                        <Checkbox
                          checked={businessTypes.includes(t.name)}
                          onCheckedChange={() => toggleBusinessType(t.name)}
                        />
                        <span className="text-sm">{t.name}</span>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
              )}
              <p className="text-xs text-muted-foreground" data-testid="text-bt-count">
                {businessTypes.length} selected
              </p>
            </div>
          )}

          {/* ── Step 2: Cities ──────────────────────────────────────────── */}
          {stepKey === "cities" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                {/* Seeding loading state — shown when us_cities is empty */}
                {!seedReady ? (
                  <div className="flex flex-col items-center justify-center h-[300px] rounded-md border border-dashed gap-3 text-center px-4" data-testid="status-seeding">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <p className="text-sm font-medium">Seeding city data, one moment…</p>
                    <p className="text-xs text-muted-foreground">
                      This only happens once on a fresh environment.
                    </p>
                  </div>
                ) : (
                  <>
                    <Label>Pick states</Label>
                    {/* State search */}
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        className="pl-8"
                        placeholder="Search states…"
                        value={stateSearch}
                        onChange={(e) => setStateSearch(e.target.value)}
                        data-testid="input-state-search"
                      />
                    </div>
                    {/* Multi-select state list */}
                    <ScrollArea className="h-[180px] rounded-md border p-1" data-testid="list-states">
                      <div className="space-y-0.5">
                        {statesLoading ? (
                          <div className="flex items-center justify-center h-[140px] text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />Loading…
                          </div>
                        ) : states
                            .filter((s) =>
                              !stateSearch.trim() ||
                              s.state.toLowerCase().includes(stateSearch.trim().toLowerCase()) ||
                              s.stateAbbrev.toLowerCase().includes(stateSearch.trim().toLowerCase()),
                            )
                            .map((s) => (
                              <div
                                key={s.stateAbbrev}
                                className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover-elevate"
                                data-testid={`option-state-${s.stateAbbrev}`}
                              >
                                <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                  <Checkbox
                                    checked={activeStates.has(s.stateAbbrev)}
                                    onCheckedChange={() =>
                                      setActiveStates((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(s.stateAbbrev)) next.delete(s.stateAbbrev);
                                        else next.add(s.stateAbbrev);
                                        return next;
                                      })
                                    }
                                  />
                                  <span className="text-sm truncate">{s.state}</span>
                                </label>
                                <span className="flex items-center gap-1.5 shrink-0">
                                  <button
                                    type="button"
                                    className="text-xs text-primary hover:underline focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                                    onClick={(e) => { e.preventDefault(); addAllForState(s); }}
                                    disabled={pendingAddAllStates.has(s.stateAbbrev)}
                                    data-testid={`button-add-all-state-${s.stateAbbrev}`}
                                  >
                                    {pendingAddAllStates.has(s.stateAbbrev) ? "Adding…" : "Add all"}
                                  </button>
                                  <Badge variant="secondary" className="text-xs font-normal tabular-nums">
                                    {fmt(s.cityCount)}
                                  </Badge>
                                </span>
                              </div>
                            ))}
                      </div>
                    </ScrollArea>
                    {activeStates.size > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {activeStates.size} {activeStates.size === 1 ? "state" : "states"} selected
                      </p>
                    )}
                    {/* City search + list — shown once at least one state is selected */}
                    {activeStates.size > 0 && (
                      <>
                        <div className="relative">
                          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            className="pl-8"
                            placeholder="Search cities…"
                            value={citySearch}
                            onChange={(e) => setCitySearch(e.target.value)}
                            data-testid="input-city-search"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {citiesLoading ? "Loading…" : `${fmt(cities.length)} cities`}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={addAllVisible}
                            disabled={cities.length === 0}
                            data-testid="button-add-all-cities"
                          >
                            Add all shown
                          </Button>
                        </div>
                        <ScrollArea className="h-[160px] rounded-md border p-2">
                          <div className="space-y-1">
                            {cities.map((c) => {
                              const checked = !!selectedCities[cityKey(c)];
                              return (
                                <label
                                  key={c.id}
                                  className="flex items-center justify-between gap-2 rounded-md p-1.5 hover-elevate cursor-pointer"
                                  data-testid={`option-city-${c.id}`}
                                >
                                  <span className="flex items-center gap-2">
                                    <Checkbox checked={checked} onCheckedChange={() => toggleCity(c)} />
                                    <span className="text-sm">{c.city}, {c.stateAbbrev}</span>
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {c.population ? fmt(c.population) : "—"}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </ScrollArea>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Selected basket */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Selected cities</Label>
                  <Badge variant="secondary" data-testid="badge-selected-count">
                    {selectedList.length}
                  </Badge>
                </div>
                {selectedList.length === 0 ? (
                  <div className="flex h-[300px] items-center justify-center rounded-md border border-dashed text-center text-sm text-muted-foreground">
                    Pick cities on the left.<br />They accumulate here across states.
                  </div>
                ) : (
                  <ScrollArea className="h-[300px] rounded-md border p-2">
                    <div className="flex flex-wrap gap-1.5">
                      {selectedList.map((c) => (
                        <Badge
                          key={cityKey(c)}
                          variant="outline"
                          className="gap-1"
                          data-testid={`chip-city-${cityKey(c)}`}
                        >
                          {c.city}, {c.stateAbbrev}
                          <button
                            onClick={() =>
                              setSelectedCities((prev) => {
                                const next = { ...prev };
                                delete next[cityKey(c)];
                                return next;
                              })
                            }
                            data-testid={`button-remove-city-${cityKey(c)}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          )}

          {/* ── Step 3: Options ─────────────────────────────────────────── */}
          {stepKey === "options" && (
            <div className="space-y-5 max-w-md">
              <div className="space-y-2">
                <Label htmlFor="run-name">Run name (optional)</Label>
                <Input
                  id="run-name"
                  value={runName}
                  onChange={(e) => setRunName(e.target.value)}
                  placeholder="e.g. Q3 Texas venues"
                  data-testid="input-run-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Campaign type</Label>
                <Select value={campaignType} onValueChange={(v) => setCampaignType(v as any)}>
                  <SelectTrigger data-testid="select-campaign-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drip">Cold drip sequence</SelectItem>
                    {supportsMass && <SelectItem value="mass">Mass send</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {/* Client type — tenant only. Tags every imported lead with the
                  operator's chosen type instead of the schema default. */}
              {enrollsLeads && (
                <div className="space-y-2">
                  <Label>Client type for imported leads</Label>
                  <Select value={clientType} onValueChange={setClientType}>
                    <SelectTrigger data-testid="select-client-type">
                      <SelectValue
                        placeholder={
                          clientTypes.length === 0
                            ? "No client types defined"
                            : "Choose a client type"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {clientTypes.map((c) => (
                        <SelectItem key={c.id} value={c.name}>
                          {c.name}
                          {c.isDefault ? " (default)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Every lead this run imports is tagged with this client type.
                    Pick a new name to create it on launch.
                  </p>
                </div>
              )}
              <div
                className="flex items-start gap-3 rounded-md border p-3"
                data-testid="status-verify-emails"
              >
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <div>
                  <Label className="text-sm">Deliverability verification (NeverBounce) — always on</Label>
                  <p className="text-xs text-muted-foreground">
                    Every address is verified before it enters the prospect pool.
                    Undeliverable addresses are dropped and never become recipients.
                    This compliance step can't be turned off.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Step: Outreach message (tenant only) ────────────────────── */}
          {stepKey === "message" && (
            <div className="space-y-4 max-w-xl">
              <div>
                <Label>How should qualified leads be contacted?</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Pick an existing campaign to enroll into, or compose a fresh
                  message — we'll build a one-step drip campaign from it.
                </p>
              </div>
              <RadioGroup
                value={messageSource}
                onValueChange={(v) => setMessageSource(v as "existing" | "compose")}
                className="space-y-2"
              >
                <label
                  className="flex items-start gap-3 rounded-md border p-3 hover-elevate cursor-pointer"
                  data-testid="option-message-existing"
                >
                  <RadioGroupItem value="existing" className="mt-0.5" />
                  <div className="flex-1 space-y-2">
                    <span className="text-sm font-medium">Use an existing campaign</span>
                    {messageSource === "existing" && (
                      <>
                        <Select
                          value={selectedCampaignId}
                          onValueChange={setSelectedCampaignId}
                        >
                          <SelectTrigger data-testid="select-existing-campaign">
                            <SelectValue
                              placeholder={
                                allCampaigns.length === 0
                                  ? "No campaigns available"
                                  : "Choose a campaign"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {allCampaigns.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name} ({c.type === "drip" ? "Drip" : "Mass"})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {initialCampaignId && selectedCampaignId === initialCampaignId && (
                          <p
                            className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1"
                            data-testid="hint-linked-campaign"
                          >
                            <Droplets className="h-3 w-3" />
                            Linked to this agent
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </label>
                <label
                  className="flex items-start gap-3 rounded-md border p-3 hover-elevate cursor-pointer"
                  data-testid="option-message-compose"
                >
                  <RadioGroupItem value="compose" className="mt-0.5" />
                  <div className="flex-1 space-y-2">
                    <span className="text-sm font-medium">Compose a fresh message</span>
                    {messageSource === "compose" && (
                      <div className="space-y-2">
                        <Input
                          placeholder="Subject line"
                          value={composeSubject}
                          onChange={(e) => setComposeSubject(e.target.value)}
                          data-testid="input-compose-subject"
                        />
                        <VoiceToTextTextarea
                          placeholder="Write the email body…"
                          value={composeBody}
                          onChange={(e) => setComposeBody(e.target.value)}
                          rows={6}
                          data-testid="input-compose-body"
                        />
                      </div>
                    )}
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}

          {/* ── Step 4: Review + funnel estimate ────────────────────────── */}
          {stepKey === "review" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Projected funnel — estimate before you spend</h3>
                {estimateMutation.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>

              {estimate ? (
                <>
                  <div className="space-y-1.5" data-testid="funnel-estimate">
                    <FunnelBar label="Prospects (wide net)" value={estimate.prospects} max={estimate.prospects} tone="bg-blue-500" testid="prospects" />
                    <FunnelBar label="With email found" value={estimate.withEmail} max={estimate.prospects} tone="bg-blue-400" testid="with-email" />
                    <FunnelBar label="Deliverable (verified)" value={estimate.deliverable} max={estimate.prospects} tone="bg-cyan-500" testid="deliverable" />
                    <FunnelBar label="Qualified (sifted down)" value={estimate.qualified} max={estimate.prospects} tone="bg-emerald-500" testid="qualified" />
                    <FunnelBar label="Enrolled in outreach" value={estimate.enrolled} max={estimate.prospects} tone="bg-emerald-600" testid="enrolled" />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <StatCard label="Searches" value={fmt(estimate.cells)} sub={`${businessTypes.length} types × ${selectedList.length} cities`} testid="stat-searches" />
                    <StatCard label="Est. true cost" value={usd(estimate.estimatedCostUsd)} sub="our underlying spend" testid="stat-cost" />
                    <StatCard
                      label="Cost per enrolled"
                      value={estimate.enrolled > 0 ? usd(estimate.estimatedCostUsd / estimate.enrolled) : "—"}
                      sub="projected"
                      testid="stat-cpl"
                    />
                  </div>

                  {overBudget && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="warning-budget">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        This scope is projected at {usd(estimate.estimatedCostUsd)}, above the{" "}
                        {usd(BUDGET_WARN_USD)} first-run guideline. Consider trimming cities or
                        business types.
                      </span>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Estimates use conservative ratios (≈{estimate.rates.avgProspectsPerSearch}{" "}
                    prospects/search, {Math.round(estimate.rates.emailFoundRate * 100)}% email-found,{" "}
                    {Math.round(estimate.rates.deliverableRate * 100)}% deliverable,{" "}
                    {Math.round(estimate.rates.qualifiedRate * 100)}% qualified). Every run records its
                    real numbers — the estimate-vs-actual view recalibrates these over time.
                  </p>
                </>
              ) : (
                <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
                  {estimateMutation.isPending ? "Calculating estimate…" : "Add scope to see the estimate."}
                </div>
              )}

              {/* Required per-run spend cap (no default) — only relevant when
                  this surface can actually launch (spend money). The tenant
                  surface is draft-only until sending lands in a later phase. */}
              {canLaunch && (
              <div className="space-y-2 rounded-md border p-3">
                <Label htmlFor="spend-cap">Per-run spend cap (USD) — required</Label>
                <Input
                  id="spend-cap"
                  type="number"
                  min="1"
                  step="1"
                  inputMode="decimal"
                  placeholder="e.g. 20"
                  value={spendCapUsd}
                  onChange={(e) => setSpendCapUsd(e.target.value)}
                  data-testid="input-spend-cap"
                />
                <p className="text-xs text-muted-foreground">
                  There is no default. The run tracks real provider spend and{" "}
                  <strong>auto-pauses</strong> the instant it crosses this cap — you can
                  resume it later with a higher cap.
                </p>
                {capValid && estimate && capValue < estimate.estimatedCostUsd && (
                  <div
                    className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300"
                    data-testid="warning-cap-below-estimate"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      Your cap ({usd(capValue)}) is below the projected cost (
                      {usd(estimate.estimatedCostUsd)}). The run will likely pause before
                      covering the full scope.
                    </span>
                  </div>
                )}
              </div>
              )}
              {needsWarmAck && (
                <div
                  className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                  data-testid="warning-warm-fallback"
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      This workspace has no verified cold sending domain. Cold
                      outreach will fall back to a connected warm mailbox, which
                      can affect that inbox's reputation. Set up a verified cold
                      domain for safe, high-volume sending.
                    </span>
                  </div>
                  <label
                    className="flex items-center gap-2 cursor-pointer pl-6"
                    data-testid="checkbox-warm-fallback-ack"
                  >
                    <Checkbox
                      checked={warmFallbackAck}
                      onCheckedChange={(c) => setWarmFallbackAck(!!c)}
                    />
                    <span className="text-xs">
                      I understand and want to send via a warm mailbox.
                    </span>
                  </label>
                </div>
              )}
              {coldBlocked && (
                <div
                  className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                  data-testid="warning-cold-blocked"
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Cold sending isn't configured yet. The dedicated cold relay
                    must be set up before a cold run can launch — there is no
                    warm-mailbox fallback for cold bulk. You can still save a
                    reviewable draft.
                  </span>
                </div>
              )}
              {!canLaunch && estimate && (
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="text-draft-only-note"
                >
                  This saves a reviewable draft — no outreach is sent and no
                  credits are spent. Sending will be enabled separately.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => (step === 0 ? onOpenChange(false) : setStep((s) => s - 1))}
            data-testid="button-wizard-back"
          >
            {step === 0 ? "Cancel" : (<><ChevronLeft className="h-4 w-4 mr-1" /> Back</>)}
          </Button>
          {step < lastStep ? (
            <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="button-wizard-next">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant={canLaunch ? "outline" : "default"}
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || launchMutation.isPending || !estimate}
                data-testid="button-create-run"
              >
                {createMutation.isPending ? "Saving…" : "Save as draft"}
              </Button>
              {canLaunch && (
                <Button
                  onClick={() => launchMutation.mutate()}
                  disabled={
                    launchMutation.isPending ||
                    createMutation.isPending ||
                    !estimate ||
                    !capValid ||
                    !messageValid ||
                    !warmAckOk
                  }
                  data-testid="button-launch-run"
                >
                  <Rocket className="h-4 w-4 mr-2" />
                  {launchMutation.isPending ? "Launching…" : "Launch run"}
                </Button>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FunnelBar({
  label,
  value,
  max,
  tone,
  testid,
}: {
  label: string;
  value: number;
  max: number;
  tone: string;
  testid: string;
}) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-40 shrink-0 text-xs text-muted-foreground">{label}</span>
      <div className="relative h-6 flex-1 overflow-hidden rounded bg-muted">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
        <span
          className="absolute inset-y-0 left-2 flex items-center text-xs font-medium text-foreground"
          data-testid={`funnel-${testid}`}
        >
          {value.toLocaleString("en-US")}
        </span>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  testid,
}: {
  label: string;
  value: string;
  sub: string;
  testid: string;
}) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold" data-testid={testid}>{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}
